import React, { useRef } from 'react'
import {
    defineBlock,
    useEditor,
    useInspectorControl,
    ChildrenBlock,
    smartProps,
    useSmartProp,
    BlockWrapper,
} from '@smartfire/js-block-engine'
import { InnerBlocks } from '@wordpress/block-editor'
import { ResizableBox } from '@wordpress/components'
import style from './block.module.scss'
import classnames from 'classnames'
import Inspector from './Inspector'

/** SMARTFIRE STANDARD 2 COLUMNS
 *
 *
 * new @SmartProps :
 * - reverseStack : true if first on top, false if second on top
 * - stack : values 'none', 'talet' or 'mobile'
 */

/** THE BLOCK
 *
 * The Standard Two Columns is a block made of two standard columns next to each other. A resizable handle in the admin view
 * lets the admin set the size of the column on the left hand side in %. The second one on the right hand side then has a width of
 * ( 100% - width of the first ).
 *
 * A default template is set with a header and a paragraph in the first column and a cover component in the second column.
 * The two standard columns have their isResizable smartprop set to false so there aren't three resizable handles
 */

function Block() {
    const [resizableWidth, setResizableWidth] = useSmartProp('resizableWidth')
    const [displayedWidth, setDisplayedWidth] = useSmartProp('displayedWidth')
    const [parentWidth, setParentWidth] = useSmartProp('parentWidth')
    const [reverseStack] = useSmartProp('reverseStack')
    const [stack] = useSmartProp('stack')

    useInspectorControl(Inspector)
    const { isEditing } = useEditor()
    const parentRef = useRef()

    const container = classnames({
        [style.block]: true,
        [style.isEditing]: isEditing,
        [style.notEditing]: !isEditing,
    })

    const TWO_COL_TEMPLATE = [
        [
            'smartfire/standard---standard-column',
            { customClass: 'resizable', isResizable: false },
            [
                ['core/heading', { placeholder: 'Title', align: 'left' }],
                ['core/paragraph', { placeholder: 'My custom text...', align: 'left' }],
            ],
        ],
        ['smartfire/standard---standard-column', { isResizable: false }, [['core/cover']]],
    ]

    return (
        <BlockWrapper.Div className={container}>
            {isEditing ? (
                <>
                    <div
                        className={classnames({
                            [style.columnsWrapper]: true,
                            [style.reverse]: reverseStack,
                            [style.stackTablet]: stack === 'tablet',
                            [style.stackMobile]: stack === 'mobile',
                        })}
                        ref={parentRef}
                        style={{ '--resizable-width': resizableWidth + '%' }}
                    >
                        <InnerBlocks template={TWO_COL_TEMPLATE} renderAppender={false} />
                        <ResizableBox
                            size={{ width: resizableWidth + '%', height: '100%' }}
                            minHeight="50"
                            minWidth="50"
                            maxWidth="100%"
                            className={style.resizableBox}
                            style={{ '--displayed-width': displayedWidth }} //This communicates our js variable to the css
                            showHandle={true}
                            enable={{
                                top: false,
                                right: true,
                                bottom: false,
                                left: false,
                                topRight: false,
                                bottomRight: false,
                                bottomLeft: false,
                                topLeft: false,
                            }}
                            onResizeStart={() => {
                                setParentWidth(parentRef.current.clientWidth)
                            }}
                            onResize={(event, direction, resize_element, delta) => {
                                setDisplayedWidth(
                                    parseInt(
                                        resizableWidth + (delta.width / parentWidth) * 100,
                                        10,
                                    ),
                                )
                            }}
                            onResizeStop={(event, direction, resize_element, delta) => {
                                setResizableWidth(
                                    parseInt(
                                        resizableWidth + (delta.width / parentWidth) * 100,
                                        10,
                                    ),
                                )
                            }}
                        />
                    </div>
                </>
            ) : (
                <div
                    className={classnames({
                        [style.columnsWrapper]: true,
                        [style.reverse]: reverseStack,
                        [style.stackTablet]: stack === 'tablet',
                        [style.stackMobile]: stack === 'mobile',
                    })}
                    style={{ '--resizable-width': resizableWidth + '%' }}
                >
                    <ChildrenBlock />
                </div>
            )}
        </BlockWrapper.Div>
    )
}

export default defineBlock({
    Block,
    smartProps: {
        resizableWidth: smartProps.number.withDefault(50),
        displayedWidth: smartProps.number.withDefault(50),
        parentWidth: smartProps.number.withDefault(1000),
        reverseStack: smartProps.bool.withDefault(false),
        stack: smartProps.string.withDefault('none'),
    },
    setup: {
        title: 'Standard 2 Columns',
        example: {
            innerBlocks: [
                {
                    name: 'core/paragraph',
                },
            ],
        },
    },
})
